// Plain functions only: presets/ocv.js imports this file while nuxt.config.ts loads,
// so no Nuxt auto-import and no browser API here.

export type ResolvedThemeColors = {
  primary: string;
  secondary: string;
  menuHover?: string;
};

export type ColorScale = Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950, string>;

export const DEFAULT_THEME_COLORS: ResolvedThemeColors = {
  primary: "#172033",
  secondary: "#10bb82",
};

// Menu hover color when the user picked none: the accent at 12 %, as a PrimeVue token reference.
export const DEFAULT_MENU_HOVER = "color-mix(in srgb, {secondary.500}, transparent 88%)";

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const cleaned = value.trim().replace(/^#/, "");

  if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    return `#${cleaned.split("").map(char => `${char}${char}`).join("")}`.toLowerCase();
  }

  if (/^[0-9a-fA-F]{6}$/.test(cleaned)) {
    return `#${cleaned}`.toLowerCase();
  }

  return null;
}

export function isThemeColorHex(value: unknown): boolean {
  return normalizeHexColor(value) !== null;
}

function hexToRgb(hex: string) {
  const normalized = normalizeHexColor(hex) ?? "#000000";
  const value = normalized.replace("#", "");

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16),
  };
}

export function getContrastColor(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  return luminance > 0.58 ? "#111827" : "#ffffff";
}

function toHex(value: number) {
  return Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, "0");
}

function mix(hex: string, targetHex: string, targetWeight: number) {
  const source = hexToRgb(hex);
  const target = hexToRgb(targetHex);
  const sourceWeight = 1 - targetWeight;

  return `#${toHex(source.r * sourceWeight + target.r * targetWeight)}${toHex(
    source.g * sourceWeight + target.g * targetWeight,
  )}${toHex(source.b * sourceWeight + target.b * targetWeight)}`;
}

export function buildPrimaryScale(primary: string): ColorScale {
  return {
    50: mix(primary, "#ffffff", 0.96),
    100: mix(primary, "#ffffff", 0.92),
    200: mix(primary, "#ffffff", 0.84),
    300: mix(primary, "#ffffff", 0.72),
    400: mix(primary, "#ffffff", 0.56),
    500: mix(primary, "#ffffff", 0.38),
    600: mix(primary, "#ffffff", 0.24),
    700: mix(primary, "#ffffff", 0.14),
    800: mix(primary, "#ffffff", 0.06),
    900: primary,
    950: mix(primary, "#000000", 0.38),
  };
}

export function buildSecondaryScale(secondary: string): ColorScale {
  return {
    50: mix(secondary, "#ffffff", 0.9),
    100: mix(secondary, "#ffffff", 0.78),
    200: mix(secondary, "#ffffff", 0.62),
    300: mix(secondary, "#ffffff", 0.42),
    400: mix(secondary, "#ffffff", 0.2),
    500: secondary,
    600: mix(secondary, "#000000", 0.18),
    700: mix(secondary, "#000000", 0.34),
    800: mix(secondary, "#000000", 0.5),
    900: mix(secondary, "#000000", 0.62),
    950: mix(secondary, "#000000", 0.72),
  };
}
